import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingDown, DollarSign, Target, Trophy, Calendar, 
  CheckCircle2, Clock, ArrowRight, Sparkles, PiggyBank
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SavingsTracker = () => {
  const { shouldShowData } = useDemoData();

  const [savingsData, setSavingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('year');

  useEffect(() => {
    fetchSavingsData();
  }, [selectedPeriod]);

  const fetchSavingsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/savings/summary?period=${selectedPeriod}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSavingsData(data);
      }
    } catch (error) {
      console.error('Error fetching savings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImplementAction = (action) => {
    toast.success(`Started implementing: ${action.title}`);
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Savings Tracker" description="Connect your cloud account to see how much you're saving month over month." />;
  }

  if (loading || !savingsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  // Defensive defaults — backend response shape can vary; never let render explode.
  const totalSaved = savingsData.totalSaved ?? 0;
  const savingsGoal = savingsData.savingsGoal || 1;
  const monthlyAverage = savingsData.monthlyAverage ?? 0;
  const implementedActions = savingsData.implementedActions ?? 0;
  const pendingActions = savingsData.pendingActions ?? 0;
  const savingsTrend = Array.isArray(savingsData.savingsTrend) ? savingsData.savingsTrend : [];
  const categoryBreakdown = Array.isArray(savingsData.categoryBreakdown) ? savingsData.categoryBreakdown : [];
  const pendingOpportunities = Array.isArray(savingsData.pendingOpportunities) ? savingsData.pendingOpportunities : [];
  const recentActions = Array.isArray(savingsData.recentActions) ? savingsData.recentActions : [];

  const goalProgress = ((totalSaved / savingsGoal) * 100).toFixed(1);

  return (
    <div className="space-y-6" data-testid="savings-tracker-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <PiggyBank className="w-7 h-7 text-emerald-500" />
            Savings Tracker
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">
            Track your cost optimization progress and savings
          </p>
        </div>
        <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-slate-800 bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-900">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Total Saved</p>
                <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">${totalSaved.toLocaleString()}</p>
              </div>
              <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Savings Goal</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">${savingsGoal.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Target className="w-6 h-6 text-emerald-600 dark:text-blue-400" />
              </div>
            </div>
            <Progress value={parseFloat(goalProgress)} className="mt-3 h-2" />
            <p className="text-xs text-slate-400 mt-1">{goalProgress}% of goal reached</p>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Monthly Average</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">${monthlyAverage.toLocaleString()}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-emerald-600 dark:text-cyan-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Actions Implemented</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{implementedActions}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-2">{pendingActions} pending opportunities</p>
          </CardContent>
        </Card>
      </div>

      {/* Savings Trend Chart */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Savings Trend</CardTitle>
          <CardDescription>Monthly savings achieved vs projected</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={savingsTrend}>
              <defs>
                <linearGradient id="colorSaved" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-slate-700" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
              <Tooltip 
                formatter={(value) => [`$${value.toLocaleString()}`, '']}
                contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '8px' }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="saved" 
                stroke="#10b981" 
                fillOpacity={1} 
                fill="url(#colorSaved)" 
                name="Actual Savings"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="projected" 
                stroke="#3b82f6" 
                strokeDasharray="5 5" 
                name="Projected"
                strokeWidth={2}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Savings by Category */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle>Savings by Category</CardTitle>
            <CardDescription>Breakdown of savings by optimization type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {categoryBreakdown.map((cat, index) => (
                <div key={index}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{cat.category}</span>
                    <span className="text-sm font-semibold text-emerald-600">${cat.saved.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={cat.percent} className="h-2 flex-1" />
                    <span className="text-xs text-slate-400 w-12 text-right">{cat.percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pending Opportunities */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Pending Opportunities
            </CardTitle>
            <CardDescription>Actions waiting to be implemented</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingOpportunities.map((opp, oIdx) => (
                <div 
                  key={opp.id ?? `opp-${oIdx}`}
                  className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-emerald-500 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900 dark:text-white text-sm">{opp.title}</h4>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-emerald-600 font-semibold text-sm">
                          +${opp.potentialSavings.toLocaleString()}/mo
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {opp.effort} effort
                        </Badge>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => handleImplementAction(opp)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Actions */}
      <Card className="border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Recent Savings Actions</CardTitle>
          <CardDescription>History of implemented optimizations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {savingsData.recentActions.map((action) => (
              <div 
                key={action.id}
                className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white">{action.title}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-3 h-3 text-slate-400" />
                      <span className="text-xs text-slate-400">{action.date}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-emerald-600">+${action.savings.toLocaleString()}</p>
                  <p className="text-xs text-slate-400">monthly savings</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SavingsTracker;
